import React, { useState, useEffect } from 'react';
import { useElapsedTimer } from '../hooks/useElapsedTimer';
import { Slider } from './ui/slider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Zap, Layers, Volume2, AlertCircle, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { axiosWithRetry } from '../utils/withRetry';
import { API } from '../constants';

/**
 * SlicerPanel - Advanced 3D Slicer Controls
 * Uses /slice endpoint for real-time estimation
 */
export function SlicerPanel({
  stlFile,
  material = 'PLA',
  infill = 20,
  scale = 1.0,
  quote,

  onCalculate,
  isCalculating = false,
  onUploadComplete = true,  // Wait until upload is complete
  isSlicing = false,
  onSlicingChange,
  onProgressChange,
  onElapsedChange,
  onEstimatedChange
}) {
  
  // State
  const selectedQuality = 'normal'; // Fixed quality setting
  const [parameters, setParameters] = useState({
    layer_height: 0.2
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [estimatedDuration, setEstimatedDuration] = useState(30);

  // Estimate slicer duration from file size (empirical: ~5s base + 1s per 100KB)
  const estimateDuration = (file) => {
    if (!file) return 30;
    const sizeMB = file.size / (1024 * 1024);
    // Clamp between 5s and 180s
    return Math.min(180, Math.max(5, Math.round(5 + sizeMB * 12)));
  };

  // Elapsed timer via custom hook – must be declared before animatedProgress useMemo
  const elapsed = useElapsedTimer(isLoading, onElapsedChange);

  // Animated progress (10% → 90% over estimated time, then holds until done)
  const animatedProgress = React.useMemo(() => {
    if (!isLoading) return 0;
    const ratio = Math.min(elapsed / estimatedDuration, 1);
    // Ease-out: fast start, slows near 90%
    return Math.round(10 + 80 * (1 - Math.pow(1 - ratio, 2)));
  }, [isLoading, elapsed, estimatedDuration]);

  // Keep parent progress in sync
  React.useEffect(() => {
    if (isLoading) onProgressChange?.(animatedProgress);
  }, [animatedProgress, isLoading]);

  // Propagate estimated duration to parent
  React.useEffect(() => {
    onEstimatedChange?.(estimatedDuration);
  }, [estimatedDuration]);

  // Auto-calculate on parameter change (only after upload complete)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (stlFile && onUploadComplete) {  // Wait for upload of large files
        calculateSlice();
      }
    }, 800); // Debounce 800ms
    return () => clearTimeout(timer);
  }, [parameters, material, stlFile, infill, scale, onUploadComplete]);

  const calculateSlice = async () => {
    if (!stlFile) return;

    setEstimatedDuration(estimateDuration(stlFile));
    setIsLoading(true);
    onSlicingChange?.(true);  // Notify parent that slicing started
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', stlFile);
      formData.append('material', material);
      formData.append('layer_height', parameters.layer_height);
      formData.append('infill', infill);
      formData.append('scale_factor', scale);
      formData.append('quality', 'normal');

      if (process.env.NODE_ENV !== 'production') console.log('Sending to /api/slice:', {
        material,
        layer_height: parameters.layer_height,
        infill: infill,
        scale_factor: scale,
        quality: 'normal'
      });

      const res = await axiosWithRetry({
        method: 'post',
        url: `${API}/slice`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5min timeout
      });

      if (process.env.NODE_ENV !== 'production') console.log('✅ Slice API Response:', res.data);

      setResult(res.data);
      onProgressChange?.(100);
      onSlicingChange?.(false);
      onCalculate?.(res.data);

    } catch (err) {
      const errorMsg = typeof err.response?.data?.detail === 'object' ? JSON.stringify(err.response?.data?.detail) : (err.response?.data?.detail || err.message || 'Unbekannter Fehler');
      setError(errorMsg);
      if (process.env.NODE_ENV !== 'production') console.error('❌ Slicer error:', errorMsg, err);
    } finally {
      setIsLoading(false);
      onElapsedChange?.(0);
    }
  };

  const handleParameterChange = (key, value) => {
    setParameters(prev => ({
      ...prev,
      [key]: value
    }));
  };


  return (
    <div className="space-y-4">
          {isLoading ? null : result && result.estimates ? (
            <></>
          ) : (
            <Card className="border-dashed">
              <CardContent className="pt-8 pb-8 text-center text-gray-500">
                📊 Lade eine STL-Datei hoch, um Ergebnisse zu sehen
              </CardContent>
            </Card>
          )}
    </div>
);

}
export default SlicerPanel;
